<script lang="ts" setup>
import BaseTransition from "@/components/common/BaseTransition.vue";
import { CartService } from "~/services/CartService";
import { MainSearchService } from "~/services/MainSearchService";
import MainProductCard from "@/components/common/product/MainProductCard.vue";
import type MainSearchResult from "~/models/MainSearchResult";
import { computed, onMounted, ref } from "vue";
import type Product from "~/models/Product";
import { useIntersectionObserver } from "@vueuse/core";

const MAX_PAGES = 2;
const mainSearch = new MainSearchService();

const page = ref(0);
const loading = ref(true);
const searchResult = ref<MainSearchResult>();
const products = ref<Product[]>();
const maxPagesReached = computed(() => page.value >= MAX_PAGES);
const randomSeed = parseInt((Math.random() * 10000).toFixed(0));

onMounted(() => {
  const cartService = new CartService();
  cartService.getCartInitData();
});

const search = async () => {
  if (maxPagesReached.value) {
    return;
  }
  if (searchResult.value && !searchResult.value.hasMore()) {
    return;
  }
  loading.value = true;
  try {
    searchResult.value = await mainSearch.search(
      page.value + 1,
      ["oferta_cualquiera"],
      6,
      randomSeed,
    );
    products.value = searchResult.value.data;
    page.value += 1;
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const section = ref<HTMLElement>();
let searched = false;
useIntersectionObserver(
  section,
  ([observer]) => {
    if (!searched && observer?.isIntersecting) {
      searched = true;
      search();
    }
  },
  {
    // Espacio adicional para detectar la intersección en porcentaje del tamañao vertical de la pantalla.
    rootMargin:
      typeof window === "object"
        ? (window.innerHeight / 100) * 25 + "px"
        : undefined, // 25%
  },
);
</script>

<template>
  <section ref="section">
    <div
      v-if="searchResult && products && products.length > 0"
      ref="section"
      class="mt-4 mt-lg-5 px-3 px-lg-0 products-on-sale"
    >
      <h2 class="text-secondary title">Productos en oferta</h2>
      <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-gap-3 mt-3">
        <div
          v-for="(product, index) in products"
          :key="index"
          class="col gap-3"
        >
          <base-transition>
            <main-product-card :product="product" />
          </base-transition>
        </div>
      </div>
      <div
        v-if="!loading && (searchResult.hasMore() || maxPagesReached)"
        class="row px-2 mt-3"
      >
        <button
          v-if="!maxPagesReached && searchResult.hasMore()"
          class="w-100 bg-body-tertiary border-0 fs-5 text-primary p-3"
          @click="search()"
        >
          Ver más resultados
        </button>
        <router-link
          v-else
          class="btn w-100 bg-body-tertiary border-0 fs-5 text-primary p-3"
          :to="{
            name: 'search',
            params: { filters: ['oferta_cualquiera'] },
            query: { random_seed: randomSeed },
          }"
        >
          Ver todos los resultados
        </router-link>
      </div>
      <div
        v-else-if="loading"
        class="d-flex justify-content-center align-content-center p-2 mt-3"
      >
        <div class="spinner-border text-secondary" role="status">
          <span class="visually-hidden mx-auto">Loading...</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang=""></style>
